/**
 * useWishlist — react-query hook for the shopping wishlist.
 *
 * Exposes:
 *   - `ids` (Set<string>)     — product_ids the current user has hearted
 *   - `count`                 — total items
 *   - `isReady` / `isAnon`    — auth+load state (anon users get an empty set)
 *   - `toggle(product_id)`    — flip a product's saved state (optimistic)
 *   - `remove(product_id)`
 *   - `isSaved(product_id)`
 *
 * Requires the user to be logged in — anon users see the heart but tapping
 * it triggers a login prompt (see the UI layer).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { api, getToken } from "@/lib/api";

interface WishlistIdsResp { product_ids: string[]; }
interface WishlistToggleResp {
  ok: boolean;
  in_wishlist: boolean;
  total: number;
}

export function useWishlist() {
  const qc = useQueryClient();
  const authed = !!getToken();

  const idsQuery = useQuery<WishlistIdsResp>({
    queryKey: ["wishlist-ids"],
    queryFn: async () => (await api.get("/shop/wishlist/ids")).data,
    enabled: authed,
    staleTime: 30_000,
  });

  const ids = useMemo(
    () => new Set(idsQuery.data?.product_ids || []),
    [idsQuery.data]
  );

  const toggleMut = useMutation<WishlistToggleResp, unknown, string>({
    mutationFn: async (productId: string) =>
      (await api.post(`/shop/wishlist/toggle/${productId}`)).data,
    // Optimistic update — heart state should flip instantly, without
    // waiting on the network round-trip.
    onMutate: async (productId) => {
      await qc.cancelQueries({ queryKey: ["wishlist-ids"] });
      const prev = qc.getQueryData<WishlistIdsResp>(["wishlist-ids"]);
      const nextIds = new Set(prev?.product_ids || []);
      if (nextIds.has(productId)) nextIds.delete(productId);
      else nextIds.add(productId);
      qc.setQueryData<WishlistIdsResp>(["wishlist-ids"], {
        product_ids: Array.from(nextIds),
      });
      return { prev };
    },
    onError: (_e, _pid, ctx: any) => {
      if (ctx?.prev) qc.setQueryData(["wishlist-ids"], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["wishlist-ids"] });
      qc.invalidateQueries({ queryKey: ["wishlist"] });
    },
  });

  const toggle = useCallback((productId: string) => {
    if (!authed) return { requiresAuth: true } as const;
    toggleMut.mutate(productId);
    return { requiresAuth: false } as const;
  }, [authed, toggleMut]);

  const isSaved = useCallback((productId: string) => ids.has(productId), [ids]);

  return {
    ids,
    count: ids.size,
    isReady: !authed || idsQuery.isSuccess,
    isAnon: !authed,
    toggle,
    isSaved,
    isPending: toggleMut.isPending,
  };
}
